import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// Zod schema for webhook form validation
const webhookFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .trim(),
  url: z
    .string()
    .min(1, 'URL is required')
    .url('Please enter a valid URL')
    .trim(),
  events: z.array(z.string()).min(1, 'At least one event must be selected'),
  isActive: z.boolean().default(true),
});

// Available webhook events
const WEBHOOK_EVENTS = [
  {
    id: 'contact.created',
    label: 'Contact Created',
    description: 'Triggered when a new contact is created',
  },
  {
    id: 'contact.updated',
    label: 'Contact Updated',
    description: 'Triggered when a contact is updated',
  },
  {
    id: 'contact.deleted',
    label: 'Contact Deleted',
    description: 'Triggered when a contact is deleted',
  },
  {
    id: 'message.received',
    label: 'Message Received',
    description: 'Triggered when a message is received',
  },
  {
    id: 'message.sent',
    label: 'Message Sent',
    description: 'Triggered when a message is sent',
  },
  {
    id: 'conversation.created',
    label: 'Conversation Created',
    description: 'Triggered when a new conversation is created',
  },
  {
    id: 'conversation.updated',
    label: 'Conversation Updated',
    description: 'Triggered when a conversation is updated',
  },
  {
    id: 'project.created',
    label: 'Project Created',
    description: 'Triggered when a new project is created',
  },
  {
    id: 'project.updated',
    label: 'Project Updated',
    description: 'Triggered when a project is updated',
  },
];

/**
 * Webhook Form Component - Reusable form for creating and editing webhooks
 * @param {Object} props - Component props
 * @param {Object} props.webhook - Webhook data for editing (optional)
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.isLoading - Loading state
 * @returns {JSX.Element} Webhook form component
 */
const WebhookForm = ({ webhook, onSubmit, onCancel, isLoading = false }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(webhookFormSchema),
    defaultValues: {
      name: '',
      url: '',
      events: [],
      isActive: true,
    },
  });

  const watchedEvents = watch('events');

  // Initialize form data when webhook prop changes (for editing)
  useEffect(() => {
    if (webhook) {
      setValue('name', webhook.name || '');
      setValue('url', webhook.url || '');
      setValue('events', webhook.events || []);
      setValue(
        'isActive',
        webhook.isActive !== undefined ? webhook.isActive : true
      );
    } else {
      reset();
    }
  }, [webhook, setValue, reset]);

  // Handle form submission
  const onFormSubmit = data => {
    onSubmit(data);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {webhook ? 'Edit Webhook' : 'Create New Webhook'}
        </h2>
        <p className="text-sm text-gray-500">
          {webhook
            ? 'Update webhook configuration'
            : 'Configure webhook settings'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
        {/* Name Field */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Webhook Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            {...register('name')}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.name
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300'
            }`}
            placeholder="e.g., Contact Events Webhook, Message Notifications"
            disabled={isLoading || isSubmitting}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* URL Field */}
        <div>
          <label
            htmlFor="url"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Webhook URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            id="url"
            {...register('url')}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.url
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300'
            }`}
            placeholder="https://your-domain.com/webhooks/events"
            disabled={isLoading || isSubmitting}
          />
          {errors.url && (
            <p className="mt-1 text-sm text-red-600">{errors.url.message}</p>
          )}
        </div>

        {/* Events Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Events <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3 max-h-60 overflow-y-auto border border-gray-300 rounded-md p-4">
            {WEBHOOK_EVENTS.map(event => (
              <label
                key={event.id}
                className="flex items-start space-x-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  value={event.id}
                  {...register('events')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                  disabled={isLoading || isSubmitting}
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900">
                    {event.label}
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {event.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
          {errors.events && (
            <p className="mt-1 text-sm text-red-600">{errors.events.message}</p>
          )}
          {watchedEvents && watchedEvents.length > 0 && (
            <p className="mt-2 text-sm text-gray-500">
              Selected: {watchedEvents.length} event
              {watchedEvents.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Active Status Field */}
        <div>
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              {...register('isActive')}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              disabled={isLoading || isSubmitting}
            />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Inactive webhooks will not receive any events
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Webhook Configuration
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Your webhook endpoint should be able to receive POST requests
                  with JSON payloads. Make sure your endpoint is publicly
                  accessible and can handle the selected events.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            disabled={isLoading || isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            disabled={isLoading || isSubmitting}
          >
            {isLoading || isSubmitting ? (
              <div className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {webhook ? 'Updating...' : 'Creating...'}
              </div>
            ) : webhook ? (
              'Update Webhook'
            ) : (
              'Create Webhook'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WebhookForm;
